(() => {
  const cartRoot = window.Shopify?.routes?.root || '/';
  const formatUSD = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(cents / 100);
  let cartState;
  let cartRequest;
  let refreshTimer;
  let cartRevision = 0;
  const progressBars = () => [...document.querySelectorAll('ih-order-progress')];
  const minimum = () => Number(progressBars()[0]?.dataset.minimum || 0);
  const cartBusy = () => Boolean(document.querySelector('.cart__items--disabled, product-form button.loading'));
  const eligible = (cart) => cart?.item_count > 0 && (!cart.minimum_enabled || cart.business_item_count === 0 || (cart.currency === 'USD' && cart.business_total_price >= cart.minimum_cents));

  const renderCartState = (cart) => {
    cartState = cart;
    const hasBusiness = cart.business_item_count > 0;
    progressBars().forEach((bar) => {
      const goal = cart.minimum_cents;
      const usd = cart.currency === 'USD';
      const remaining = Math.max(0, goal - cart.business_total_price);
      const percent = usd && goal > 0 ? Math.min(100, Math.max(0, cart.business_total_price / goal * 100)) : 0;
      const template = !usd ? bar.dataset.currencyMessage : remaining ? bar.dataset.remainingMessage : bar.dataset.completeMessage;
      bar.hidden = !hasBusiness;
      bar.style.display = hasBusiness ? '' : 'none';
      bar.querySelector('[data-minimum-message]').textContent = template.replace('__AMOUNT__', formatUSD(remaining)).replace('__MINIMUM__', formatUSD(goal));
      bar.querySelector('[data-minimum-fill]').style.width = `${percent}%`;
      bar.querySelector('[role=progressbar]').hidden = false;
      bar.querySelector('[role=progressbar]').setAttribute('aria-valuenow', String(Math.round(percent)));
      bar.dataset.state = !hasBusiness ? 'exempt' : usd && !remaining ? 'complete' : 'pending';
    });
    document.querySelectorAll('[data-ih-checkout]').forEach((button) => {
      button.disabled = !eligible(cart);
      button.setAttribute('aria-disabled', String(button.disabled));
      const label = button.querySelector('[data-ih-checkout-label]');
      if (label) label.textContent = hasBusiness && cart.collective_item_count === 0 ? button.dataset.businessLabel : button.dataset.checkoutLabel;
    });
    document.querySelectorAll('[data-ih-order-note]').forEach((note) => {
      note.textContent = hasBusiness ? cart.collective_item_count > 0 ? note.dataset.mixedMessage : note.dataset.businessMessage : note.dataset.retailMessage;
    });
  };

  const parseCartPolicy = (html) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const state = parsed.querySelector('[data-ih-cart-policy]');
    if (!state) throw new Error('Cart policy unavailable');
    const cart = JSON.parse(state.textContent);
    const numbers = ['item_count', 'total_price', 'business_item_count', 'business_total_price', 'collective_item_count', 'minimum_cents'];
    if (!numbers.every((key) => Number.isFinite(cart[key]) && cart[key] >= 0) || typeof cart.currency !== 'string' || typeof cart.minimum_enabled !== 'boolean') throw new Error('Cart policy invalid');
    return cart;
  };

  const readCart = (fresh = false) => {
    if (fresh && cartRequest) return cartRequest.catch(() => {}).then(() => readCart());
    if (cartRequest) return cartRequest;
    const controller = new AbortController();
    const requestRevision = cartRevision;
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    cartRequest = fetch(`${cartRoot}cart?section_id=ih-cart-policy`, { credentials: 'same-origin', headers: { Accept: 'text/html' }, cache: 'no-store', signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error('Cart unavailable'); return response.text(); })
      .then(parseCartPolicy)
      .then((cart) => {
        cart.revision = requestRevision;
        if (requestRevision === cartRevision) renderCartState(cart);
        return cart;
      })
      .finally(() => {
        window.clearTimeout(timeout);
        cartRequest = null;
        if (requestRevision !== cartRevision) queueRefresh();
      });
    return cartRequest;
  };

  const showGate = (error = false, updating = false) => {
    const bar = document.querySelector('.drawer.active ih-order-progress') || progressBars()[0];
    if (!bar) return;
    if (error || updating) {
      bar.hidden = false;
      bar.style.display = '';
      bar.querySelector('[data-minimum-message]').textContent = updating ? bar.dataset.updatingMessage : bar.dataset.errorMessage;
      bar.dataset.state = updating ? 'updating' : 'error';
      bar.querySelector('[role=progressbar]').hidden = true;
    }
    bar.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const queueRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => readCart().catch(() => {}), 100);
  };

  if (!customElements.get('ih-order-progress')) customElements.define('ih-order-progress', class extends HTMLElement {
    connectedCallback() {
      if (cartState) renderCartState(cartState);
      else renderCartState({ total_price: Number(this.dataset.total), item_count: Number(this.dataset.itemCount), business_item_count: Number(this.dataset.businessItemCount), business_total_price: Number(this.dataset.businessTotal), collective_item_count: Number(this.dataset.collectiveItemCount), currency: this.dataset.currency, minimum_enabled: true, minimum_cents: Number(this.dataset.minimum) });
      queueRefresh();
    }
  });

  const attachCartUpdates = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => { cartRevision += 1; queueRefresh(); });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attachCartUpdates, { once: true });
  else attachCartUpdates();
  if (document.querySelector('[data-ih-checkout]')) queueRefresh();

  document.addEventListener('submit', async (event) => {
    const button = event.submitter;
    const form = event.target;
    if (!minimum() || !(form instanceof HTMLFormElement) || button?.name !== 'checkout') return;
    if (form.dataset.ihMinimumApproved === 'true') { delete form.dataset.ihMinimumApproved; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (cartBusy()) { showGate(false, true); return; }
    if (form.dataset.ihMinimumChecking === 'true') return;
    form.dataset.ihMinimumChecking = 'true';
    try {
      const cart = await readCart(true);
      if (cartBusy() || cart.revision !== cartRevision) { showGate(false, true); return; }
      if (!eligible(cart)) { showGate(); return; }
      form.dataset.ihMinimumApproved = 'true';
      form.requestSubmit(button);
    } catch (error) { showGate(true); }
    finally { delete form.dataset.ihMinimumChecking; }
  }, true);

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('a[href]');
    if (!minimum() || !link) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !/\/checkout(?:\/|$)/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (cartBusy()) { showGate(false, true); return; }
    try {
      const cart = await readCart(true);
      if (cartBusy() || cart.revision !== cartRevision) { showGate(false, true); return; }
      if (eligible(cart)) window.location.assign(url.href);
      else showGate();
    } catch (error) { showGate(true); }
  }, true);

  document.addEventListener('change', (event) => {
    const select = event.target.closest('.ih-card-purchase__option');
    if (!select) return;
    const option = select.selectedOptions[0];
    const quantity = select.form.querySelector('[name=quantity]');
    quantity.min = option.dataset.quantityMin || '1';
    quantity.step = option.dataset.quantityStep || '1';
    if (option.dataset.quantityMax) quantity.max = option.dataset.quantityMax;
    else quantity.removeAttribute('max');
    quantity.value = quantity.min;
  });

  const initializeFeeds = (scope = document) => {
    const roots = [...scope.querySelectorAll('[data-ih-infinite]')];
    if (scope.matches?.('[data-ih-infinite]')) roots.unshift(scope);
    roots.forEach((root) => {
      if (root.dataset.ihBound) return;
      root.dataset.ihBound = 'true';
      const grid = root.querySelector('[data-ih-infinite-grid]');
      const sentinel = root.querySelector('[data-ih-infinite-sentinel]');
      const link = root.querySelector('[data-ih-infinite-next]');
      const status = root.querySelector('[data-ih-infinite-status]');
      if (!link || !root.dataset.nextUrl) return;
      const seen = new Set([...grid.querySelectorAll('[data-product-id]')].map((item) => item.dataset.productId));
      let nextUrl = root.dataset.nextUrl;
      let busy = false;
      let observer;
      let controller;
      let stopped = false;
      const load = async () => {
        if (busy || !nextUrl || stopped) return;
        busy = true;
        sentinel.dataset.state = 'loading';
        grid.setAttribute('aria-busy', 'true');
        status.textContent = root.dataset.loadingMessage;
        controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);
        try {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', root.dataset.sectionId);
          const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Catalog unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextRoot = parsed.querySelector('[data-ih-infinite]');
          if (!nextRoot) throw new Error('Catalog missing');
          let count = 0;
          nextRoot.querySelectorAll('[data-ih-infinite-item]').forEach((item) => {
            if (seen.has(item.dataset.productId)) return;
            seen.add(item.dataset.productId);
            grid.appendChild(item);
            count++;
          });
          nextUrl = nextRoot.dataset.nextUrl;
          root.dataset.nextUrl = nextUrl;
          sentinel.dataset.state = nextUrl ? 'idle' : 'complete';
          status.textContent = root.dataset.loadedMessage.replace('__COUNT__', String(count));
          if (nextUrl) link.href = nextUrl;
          else { link.remove(); observer?.disconnect(); }
        } catch (error) {
          if (stopped) return;
          sentinel.dataset.state = 'retry';
          status.textContent = root.dataset.retryMessage;
        } finally {
          window.clearTimeout(timeout);
          busy = false;
          grid.setAttribute('aria-busy', 'false');
        }
      };
      link.addEventListener('click', (event) => { event.preventDefault(); load(); });
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting) && sentinel.dataset.state !== 'retry') load();
        }, { rootMargin: '450px 0px', threshold: 0.01 });
        observer.observe(sentinel);
      }
      const stop = () => { stopped = true; controller?.abort(); observer?.disconnect(); };
      document.addEventListener('shopify:section:unload', (event) => { if (event.target.contains(root)) stop(); });
      window.addEventListener('pagehide', stop, { once: true });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeFeeds(), { once: true });
  else initializeFeeds();
  document.addEventListener('shopify:section:load', (event) => initializeFeeds(event.target));
})();
